import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const fmt = (n) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n || 0);
const fmtFull = (n) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

// ── Repayment helpers ──────────────────────────────────────────────────────
// All functions now accept totalMonths (not years) as the term parameter
function calcRepayment(principal, annualRate, totalMonths) {
  if (!principal || !annualRate || !totalMonths) return 0;
  const r = annualRate / 100 / 12;
  const n = totalMonths;
  if (r === 0) return principal / n;
  return (principal * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
}

function calcInterestOnly(principal, annualRate) {
  if (!principal || !annualRate) return 0;
  return (principal * (annualRate / 100)) / 12;
}

// Returns array of yearly data points; length = number of years in schedule
function buildAmortisation(principal, annualRate, totalMonths, monthlyOverpayment) {
  if (!principal || !annualRate || !totalMonths) return [];
  const r = annualRate / 100 / 12;
  const base = calcRepayment(principal, annualRate, totalMonths);
  let balance = principal;
  const data = [];
  for (let month = 1; month <= totalMonths; month++) {
    if (balance <= 0) break;
    const interest = balance * r;
    const capital = Math.min(base - interest + (monthlyOverpayment || 0), balance);
    balance = Math.max(balance - capital, 0);
    const year = Math.ceil(month / 12);
    const existing = data.find(d => d.year === year);
    if (existing) {
      existing.interest += interest;
      existing.capital += capital;
      existing.balance = balance;
      existing.months++;
    } else {
      data.push({ year, interest, capital, balance, months: 1 });
    }
  }
  return data;
}

// Returns total months actually used in the amortisation schedule
function amortTotalMonths(amortData) {
  if (!amortData || amortData.length === 0) return 0;
  const fullYears = amortData.length - 1;
  const lastEntry = amortData[amortData.length - 1];
  return fullYears * 12 + (lastEntry.months || 12);
}

// ── Stamp Duty helpers — April 2025 rates (current throughout 2026) ────────
const STANDARD_BANDS = [
  { from: 0,        to: 125000,   rate: 0.00 },
  { from: 125000,   to: 250000,   rate: 0.02 },
  { from: 250000,   to: 925000,   rate: 0.05 },
  { from: 925000,   to: 1500000,  rate: 0.10 },
  { from: 1500000,  to: Infinity, rate: 0.12 },
];

const FTB_BANDS = [
  { from: 0,      to: 300000, rate: 0.00 },
  { from: 300000, to: 500000, rate: 0.05 },
];

function applyBands(price, bands, surcharge) {
  let total = 0;
  const breakdown = [];
  for (const band of bands) {
    if (price <= band.from) break;
    const taxable = Math.min(price, band.to) - band.from;
    const effectiveRate = band.rate + surcharge;
    const tax = taxable * effectiveRate;
    total += tax;
    if (taxable > 0) {
      breakdown.push({ from: band.from, to: Math.min(price, band.to), taxable, rate: effectiveRate * 100, tax });
    }
  }
  return { total, bands: breakdown };
}

function calcSDLT(price, buyerType) {
  if (!price || price <= 0) return { total: 0, bands: [], ftbReliefLost: false };
  if (buyerType === 'ftb') {
    if (price > 500000) return { ...applyBands(price, STANDARD_BANDS, 0), ftbReliefLost: true };
    return { ...applyBands(price, FTB_BANDS, 0), ftbReliefLost: false };
  }
  if (buyerType === 'additional') return { ...applyBands(price, STANDARD_BANDS, 0.05), ftbReliefLost: false };
  return { ...applyBands(price, STANDARD_BANDS, 0), ftbReliefLost: false };
}

const BUYER_TYPES = [
  { key: 'ftb',        label: 'First Time Buyer',       desc: '0% up to £300k · 5% on £300k–£500k · Standard rates above £500k' },
  { key: 'standard',   label: 'Standard Purchase',      desc: 'Home mover or main residence · 0% up to £125k' },
  { key: 'additional', label: 'Buy to Let / 2nd Home',  desc: 'Additional property · standard rates + 5% surcharge on all bands' },
];

export default function MortgageCalculator() {
  // Repayment state
  const [loanAmount,   setLoanAmount]   = useState('200000');
  const [termYears,    setTermYears]    = useState('25');
  const [termMonths,   setTermMonths]   = useState('0');
  const [interestRate, setInterestRate] = useState('4.5');
  const [overpayment,  setOverpayment]  = useState('');

  // Stamp duty state
  const [sdPrice,   setSdPrice]   = useState('');
  const [buyerType, setBuyerType] = useState('ftb');

  // ── Derived values ────────────────────────────────────────────────────────
  const loan          = parseFloat(loanAmount)  || 0;
  const yrs           = Math.max(parseInt(termYears)  || 0, 0);
  const mths          = Math.min(Math.max(parseInt(termMonths) || 0, 0), 11);
  const totalMonths   = yrs * 12 + mths;
  const rate          = parseFloat(interestRate) || 0;
  const over          = parseFloat(overpayment)  || 0;
  const hasValues     = loan > 0 && totalMonths > 0 && rate > 0;

  // Term display label e.g. "25 yrs 4 mo" or "25 yrs"
  const termLabel = mths > 0
    ? `${yrs} yr${yrs !== 1 ? 's' : ''} ${mths} mo`
    : `${yrs} yr${yrs !== 1 ? 's' : ''}`;

  // ── Repayment calculations ────────────────────────────────────────────────
  const repaymentMonthly    = useMemo(() => calcRepayment(loan, rate, totalMonths),          [loan, rate, totalMonths]);
  const interestOnlyMonthly = useMemo(() => calcInterestOnly(loan, rate),                    [loan, rate]);
  const totalRepayable      = useMemo(() => repaymentMonthly * totalMonths,                  [repaymentMonthly, totalMonths]);
  const totalInterest       = useMemo(() => totalRepayable - loan,                           [totalRepayable, loan]);
  const totalIOPayable      = useMemo(() => (interestOnlyMonthly * totalMonths) + loan,      [interestOnlyMonthly, totalMonths, loan]);
  const overpayMonthly      = useMemo(() => repaymentMonthly + over,                         [repaymentMonthly, over]);
  const standardAmort       = useMemo(() => buildAmortisation(loan, rate, totalMonths, 0),   [loan, rate, totalMonths]);
  const overpayAmort        = useMemo(() => over > 0 ? buildAmortisation(loan, rate, totalMonths, over) : [], [loan, rate, totalMonths, over]);
  const stdTotalInt         = useMemo(() => standardAmort.reduce((s, d) => s + d.interest, 0), [standardAmort]);
  const overTotalInt        = useMemo(() => overpayAmort.reduce((s, d) => s + d.interest, 0),  [overpayAmort]);
  const interestSaved       = useMemo(() => stdTotalInt - overTotalInt,                      [stdTotalInt, overTotalInt]);

  // Time saved with overpayments — in months, then split to yrs + mths
  const timeSavedMonths = useMemo(() => {
    if (!over || overpayAmort.length === 0) return 0;
    const stdM  = amortTotalMonths(standardAmort);
    const overM = amortTotalMonths(overpayAmort);
    return Math.max(stdM - overM, 0);
  }, [over, standardAmort, overpayAmort]);

  const savedYrs   = Math.floor(timeSavedMonths / 12);
  const savedMths  = timeSavedMonths % 12;
  const savedLabel = savedYrs > 0 && savedMths > 0
    ? `${savedYrs} yr${savedYrs !== 1 ? 's' : ''} ${savedMths} mo`
    : savedYrs > 0
    ? `${savedYrs} yr${savedYrs !== 1 ? 's' : ''}`
    : savedMths > 0
    ? `${savedMths} mo`
    : '< 1 mo';

  const overpayRemainingLabel = useMemo(() => {
    if (!over || overpayAmort.length === 0) return '';
    const m = amortTotalMonths(overpayAmort);
    const y = Math.floor(m / 12);
    const mo = m % 12;
    return mo > 0 ? `${y} yr${y !== 1 ? 's' : ''} ${mo} mo remaining` : `${y} yr${y !== 1 ? 's' : ''} remaining`;
  }, [over, overpayAmort]);

  // ── Chart data ────────────────────────────────────────────────────────────
  const chartData = useMemo(() => standardAmort.map((d, i) => ({
    year: `Yr ${d.year}`,
    'Balance (standard)': Math.round(d.balance),
    'Balance (with overpayment)': over > 0 && overpayAmort[i] ? Math.round(overpayAmort[i].balance) : undefined,
  })), [standardAmort, overpayAmort, over]);

  // ── Stamp duty calculations ───────────────────────────────────────────────
  const sdPriceNum    = parseFloat(sdPrice) || 0;
  const sdResult      = useMemo(() => calcSDLT(sdPriceNum, buyerType), [sdPriceNum, buyerType]);
  const effectiveRate = sdPriceNum > 0 ? ((sdResult.total / sdPriceNum) * 100).toFixed(2) : '0.00';

  const inputCls = 'border-slate-400 dark:border-slate-500 focus:border-red-500';

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Plus Jakarta Sans' }}>
          Mortgage Calculator
        </h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">Repayment calculator, overpayment savings and stamp duty</p>
      </div>

      <Tabs defaultValue="repayment" className="w-full">
        <TabsList className="h-auto bg-slate-100 dark:bg-slate-800 p-1 rounded-lg inline-flex">
          <TabsTrigger value="repayment">Repayment Calculator</TabsTrigger>
          <TabsTrigger value="stampduty">Stamp Duty Calculator</TabsTrigger>
        </TabsList>

        {/* ── REPAYMENT TAB ──────────────────────────────────────────────── */}
        <TabsContent value="repayment" className="mt-6 space-y-6">

          {/* Inputs */}
          <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
            <CardHeader><CardTitle className="text-base">Enter borrowing details</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="col-span-2 md:col-span-1 space-y-2">
                  <Label>Loan Amount (£)</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-medium">£</span>
                    <Input type="number" className={`pl-7 ${inputCls}`} value={loanAmount} onChange={e => setLoanAmount(e.target.value)} placeholder="200000" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Term (Years)</Label>
                  <div className="relative">
                    <Input type="number" min="0" max="40" className={inputCls} value={termYears} onChange={e => setTermYears(e.target.value)} placeholder="25" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">yrs</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Term (Months)</Label>
                  <div className="relative">
                    <Input type="number" min="0" max="11" className={inputCls} value={termMonths} onChange={e => setTermMonths(e.target.value)} placeholder="0" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">mo</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Interest Rate (%)</Label>
                  <div className="relative">
                    <Input type="number" step="0.01" className={inputCls} value={interestRate} onChange={e => setInterestRate(e.target.value)} placeholder="4.5" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">%</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {hasValues && (
            <>
              {/* Repayment vs Interest Only cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800 border-l-4 border-l-red-600">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base text-red-600">Repayment</CardTitle>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Capital & Interest · {termLabel} term</p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-slate-500 dark:text-slate-400">Monthly payment</p>
                        <p className="text-3xl font-bold text-slate-900 dark:text-white mt-1">{fmtFull(repaymentMonthly)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500 dark:text-slate-400">Total amount payable</p>
                        <p className="text-3xl font-bold text-slate-900 dark:text-white mt-1">{fmt(totalRepayable)}</p>
                      </div>
                    </div>
                    <div className="pt-2 border-t border-slate-100 dark:border-slate-700">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500 dark:text-slate-400">Total interest paid</span>
                        <span className="font-semibold text-red-600">{fmt(totalInterest)}</span>
                      </div>
                      <div className="flex justify-between text-sm mt-1">
                        <span className="text-slate-500 dark:text-slate-400">Loan amount</span>
                        <span className="font-medium text-slate-700 dark:text-slate-300">{fmt(loan)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800 border-l-4 border-l-blue-500">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base text-blue-600 dark:text-blue-400">Interest Only</CardTitle>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Balance remains unchanged · {termLabel} term</p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-slate-500 dark:text-slate-400">Monthly payment</p>
                        <p className="text-3xl font-bold text-slate-900 dark:text-white mt-1">{fmtFull(interestOnlyMonthly)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500 dark:text-slate-400">Total amount payable</p>
                        <p className="text-3xl font-bold text-slate-900 dark:text-white mt-1">{fmt(totalIOPayable)}</p>
                      </div>
                    </div>
                    <div className="pt-2 border-t border-slate-100 dark:border-slate-700">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500 dark:text-slate-400">Total interest paid</span>
                        <span className="font-semibold text-blue-600 dark:text-blue-400">{fmt(interestOnlyMonthly * totalMonths)}</span>
                      </div>
                      <div className="flex justify-between text-sm mt-1">
                        <span className="text-slate-500 dark:text-slate-400">Balance at end of term</span>
                        <span className="font-medium text-slate-700 dark:text-slate-300">{fmt(loan)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Overpayment calculator */}
              <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
                <CardHeader>
                  <CardTitle className="text-base">Overpayment Calculator</CardTitle>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">See how much time and interest you could save with a monthly overpayment</p>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="max-w-xs space-y-2">
                    <Label>Monthly Overpayment (£)</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-medium">£</span>
                      <Input type="number" className={`pl-7 ${inputCls}`} value={overpayment} onChange={e => setOverpayment(e.target.value)} placeholder="0" />
                    </div>
                  </div>
                  {over > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="bg-slate-50 dark:bg-slate-900/50 rounded-xl p-4">
                        <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">New monthly payment</p>
                        <p className="text-2xl font-bold text-slate-900 dark:text-white">{fmtFull(overpayMonthly)}</p>
                        <p className="text-xs text-slate-400 mt-1">+{fmt(over)}/month</p>
                      </div>
                      <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 border border-green-100 dark:border-green-900/40">
                        <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Interest saved</p>
                        <p className="text-2xl font-bold text-green-700 dark:text-green-400">{fmt(interestSaved)}</p>
                        <p className="text-xs text-green-600 dark:text-green-500 mt-1">less interest paid</p>
                      </div>
                      <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 border border-green-100 dark:border-green-900/40">
                        <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Term reduced by</p>
                        <p className="text-2xl font-bold text-green-700 dark:text-green-400">{savedLabel}</p>
                        <p className="text-xs text-green-600 dark:text-green-500 mt-1">{overpayRemainingLabel}</p>
                      </div>
                      <div className="bg-slate-50 dark:bg-slate-900/50 rounded-xl p-4">
                        <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Total saving</p>
                        <p className="text-2xl font-bold text-slate-900 dark:text-white">{fmt(interestSaved)}</p>
                        <p className="text-xs text-slate-400 mt-1">over the term</p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 dark:text-slate-500">Enter a monthly overpayment amount above to see your savings</p>
                  )}
                </CardContent>
              </Card>

              {/* Balance chart */}
              <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
                <CardHeader>
                  <CardTitle className="text-base">Outstanding Balance Over Time</CardTitle>
                </CardHeader>
                <CardContent>
                  <div style={{ height: 260 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={chartData} margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
                        <defs>
                          <linearGradient id="gradStd" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%"  stopColor="#e63946" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#e63946" stopOpacity={0.02} />
                          </linearGradient>
                          <linearGradient id="gradOvr" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%"  stopColor="#2a9d8f" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#2a9d8f" stopOpacity={0.02} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" opacity={0.2} />
                        <XAxis dataKey="year" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                        <YAxis tickFormatter={v => `£${(v/1000).toFixed(0)}k`} tick={{ fontSize: 11, fill: '#94a3b8' }} width={56} />
                        <Tooltip formatter={(v, n) => [fmt(v), n]} contentStyle={{ background: '#1e293b', border: 'none', borderRadius: 8, color: '#f1f5f9', fontSize: 12 }} />
                        <Legend wrapperStyle={{ fontSize: 12, color: '#94a3b8' }} />
                        <Area type="monotone" dataKey="Balance (standard)" stroke="#e63946" fill="url(#gradStd)" strokeWidth={2} dot={false} />
                        {over > 0 && <Area type="monotone" dataKey="Balance (with overpayment)" stroke="#2a9d8f" fill="url(#gradOvr)" strokeWidth={2} dot={false} />}
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {!hasValues && (
            <div className="text-center py-16 text-slate-400 dark:text-slate-500">
              <p className="text-lg">Enter your loan details above to see the calculation</p>
            </div>
          )}
        </TabsContent>

        {/* ── STAMP DUTY TAB ─────────────────────────────────────────────── */}
        <TabsContent value="stampduty" className="mt-6 space-y-6">

          <div className="flex items-start gap-3 px-4 py-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800">
            <span className="text-blue-600 dark:text-blue-400 font-bold mt-0.5">i</span>
            <p className="text-sm text-blue-700 dark:text-blue-300">
              Current SDLT rates for England & Northern Ireland — April 2025 thresholds in effect throughout 2026. No further changes announced.
            </p>
          </div>

          <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
            <CardHeader><CardTitle className="text-base">Enter property details</CardTitle></CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-2">
                  <Label>Property Purchase Price (£)</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-medium">£</span>
                    <Input type="number" className={`pl-7 ${inputCls}`} value={sdPrice} onChange={e => setSdPrice(e.target.value)} placeholder="350000" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Buyer Type</Label>
                  <div className="space-y-2">
                    {BUYER_TYPES.map(bt => (
                      <button key={bt.key} onClick={() => setBuyerType(bt.key)}
                        className={`w-full text-left px-4 py-3 rounded-lg border transition-colors ${
                          buyerType === bt.key
                            ? 'border-red-500 bg-red-50 dark:bg-red-900/20 dark:border-red-500'
                            : 'border-slate-300 dark:border-slate-600 hover:border-slate-400 dark:hover:border-slate-500 dark:bg-slate-800'
                        }`}>
                        <p className={`text-sm font-semibold ${buyerType === bt.key ? 'text-red-700 dark:text-red-300' : 'text-slate-900 dark:text-white'}`}>{bt.label}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{bt.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {sdPriceNum > 0 && (
            <>
              {buyerType === 'ftb' && sdPriceNum <= 300000 && (
                <div className="px-4 py-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                  <p className="text-sm font-medium text-green-700 dark:text-green-300">
                    ✅ Within the First Time Buyer nil-rate threshold — no stamp duty payable.
                  </p>
                </div>
              )}
              {buyerType === 'ftb' && sdPriceNum > 500000 && (
                <div className="px-4 py-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                  <p className="text-sm font-medium text-amber-700 dark:text-amber-300">
                    ⚠️ Property exceeds £500,000 — First Time Buyer relief is not available. Standard rates apply from £0.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Card className={`dark:bg-slate-800 border-l-4 ${sdResult.total === 0 ? 'border-l-green-500 border-green-200 dark:border-green-800' : 'border-l-red-600 border-slate-200 dark:border-slate-700'}`}>
                  <CardContent className="p-5">
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Stamp Duty Due</p>
                    <p className={`text-3xl font-bold ${sdResult.total === 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600'}`}>{fmt(sdResult.total)}</p>
                    <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">Effective rate: {effectiveRate}%</p>
                  </CardContent>
                </Card>
                <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
                  <CardContent className="p-5">
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Property Price</p>
                    <p className="text-3xl font-bold text-slate-900 dark:text-white">{fmt(sdPriceNum)}</p>
                    <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">{BUYER_TYPES.find(b => b.key === buyerType)?.label}</p>
                  </CardContent>
                </Card>
                <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
                  <CardContent className="p-5">
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Total Cost (inc. SDLT)</p>
                    <p className="text-3xl font-bold text-slate-900 dark:text-white">{fmt(sdPriceNum + sdResult.total)}</p>
                    <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">Price + stamp duty</p>
                  </CardContent>
                </Card>
              </div>

              <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
                <CardHeader>
                  <CardTitle className="text-base">Band by Band Breakdown</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
                          <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400">Band</th>
                          <th className="text-right px-6 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400">Taxable Amount</th>
                          <th className="text-right px-6 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400">Rate</th>
                          <th className="text-right px-6 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400">Tax</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                        {sdResult.bands.length > 0 ? sdResult.bands.map((b, i) => (
                          <tr key={i} className={b.tax === 0 ? 'bg-green-50/50 dark:bg-green-900/10' : ''}>
                            <td className="px-6 py-3 text-slate-700 dark:text-slate-300 font-medium">
                              {fmt(b.from)} — {b.to >= 1500000 ? 'above' : fmt(b.to)}
                            </td>
                            <td className="px-6 py-3 text-right text-slate-600 dark:text-slate-400">{fmt(b.taxable)}</td>
                            <td className="px-6 py-3 text-right">
                              <span className={`font-semibold ${b.tax === 0 ? 'text-green-600 dark:text-green-400' : 'text-slate-700 dark:text-slate-300'}`}>{b.rate}%</span>
                            </td>
                            <td className="px-6 py-3 text-right font-bold">
                              <span className={b.tax === 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>{fmt(b.tax)}</span>
                            </td>
                          </tr>
                        )) : (
                          <tr><td colSpan={4} className="px-6 py-4 text-center text-green-600 dark:text-green-400 font-medium">No stamp duty payable</td></tr>
                        )}
                        <tr className="bg-slate-50 dark:bg-slate-900/60 border-t-2 border-slate-300 dark:border-slate-600">
                          <td className="px-6 py-3 font-bold text-slate-900 dark:text-white">Total</td>
                          <td className="px-6 py-3 text-right font-semibold text-slate-700 dark:text-slate-300">{fmt(sdPriceNum)}</td>
                          <td className="px-6 py-3 text-right text-slate-500 dark:text-slate-400 text-xs">{effectiveRate}% effective rate</td>
                          <td className="px-6 py-3 text-right font-bold text-base text-red-600 dark:text-red-400">{fmt(sdResult.total)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {!sdPriceNum && (
            <div className="text-center py-16 text-slate-400 dark:text-slate-500">
              <p className="text-lg">Enter a property price above to calculate stamp duty</p>
            </div>
          )}

          <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
            <CardHeader>
              <CardTitle className="text-base">2026 SDLT Rate Reference</CardTitle>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">England & Northern Ireland — April 2025 thresholds, no changes for 2026</p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
                {[
                  { title: 'Standard Purchase', rows: [['£0 – £125,000','0%'],['£125,001 – £250,000','2%'],['£250,001 – £925,000','5%'],['£925,001 – £1.5m','10%'],['Above £1.5m','12%']], note: null },
                  { title: 'First Time Buyer', rows: [['£0 – £300,000','0%'],['£300,001 – £500,000','5%'],['Above £500,000','Standard rates']], note: 'Relief lost entirely above £500,000. Both buyers must be first time buyers on a joint purchase.' },
                  { title: 'Buy to Let / 2nd Home', rows: [['£0 – £125,000','5%'],['£125,001 – £250,000','7%'],['£250,001 – £925,000','10%'],['£925,001 – £1.5m','15%'],['Above £1.5m','17%']], note: 'Additional 2% surcharge applies for non-UK residents on top of all rates above.' },
                ].map(({ title, rows, note }) => (
                  <div key={title}>
                    <p className="font-semibold text-slate-900 dark:text-white mb-3">{title}</p>
                    <div className="space-y-1.5">
                      {rows.map(([band, r]) => (
                        <div key={band} className="flex justify-between text-xs">
                          <span className="text-slate-500 dark:text-slate-400">{band}</span>
                          <span className="font-semibold text-slate-700 dark:text-slate-300">{r}</span>
                        </div>
                      ))}
                    </div>
                    {note && <p className="text-xs text-slate-400 dark:text-slate-500 mt-3 leading-relaxed">{note}</p>}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

        </TabsContent>
      </Tabs>
    </div>
  );
}
